import { Volume2 } from "lucide-react";
import { speak } from "@/lib/tts";
import { ALPHABET_SOUNDS } from "@/lib/alphabetData";

// Full A–Z reference. Each tile is a button: tap → speak "A. /a/. as in apple."
export default function AZSounds({ accent = "sky" }) {
  const chipMap = {
    sky: {
      chip: "bg-sky-50 text-sky-700",
      letter: "text-sky-500",
      hoverBorder: "hover:border-sky-300",
      focusRing: "focus:ring-sky-100",
    },
    amber: {
      chip: "bg-amber-50 text-amber-700",
      letter: "text-amber-600",
      hoverBorder: "hover:border-amber-300",
      focusRing: "focus:ring-amber-100",
    },
  };
  const c = chipMap[accent] || chipMap.sky;

  const play = (item) => {
    // Browser TTS pronounces short phonetic strings ("k", "kw", "ks") as letter
    // names (e.g. "kay", "kay-double-yoo") instead of the actual phonic sound.
    // Fix: say the letter, then a natural example word twice so the learner
    // hears the sound in context.
    speak(`${item.letter}. As in ${item.word}. ${item.word}.`, { rate: 0.8 });
  };

  return (
    <section
      id="az-sounds"
      data-testid="az-sounds-section"
      className="mt-16"
    >
      <div className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest ${c.chip} px-3 py-1 rounded-full mb-3`}>
        <Volume2 className="w-3.5 h-3.5" /> A–Z sound reference
      </div>
      <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
        Every letter, every sound
      </h2>
      <p className="mt-3 text-slate-600 text-lg max-w-2xl">
        Tap any letter to hear its name, its phonetic sound, and an example
        word. A quick reference you can come back to any time.
      </p>

      <div className="mt-8 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-7 lg:grid-cols-9 gap-3">
        {ALPHABET_SOUNDS.map((item) => (
          <button
            key={item.letter}
            data-testid={`az-letter-${item.letter}`}
            onClick={() => play(item)}
            aria-label={`Play the sound of letter ${item.letter}`}
            className={`group flex flex-col items-center justify-center gap-1 p-3 rounded-2xl border-2 border-slate-100 bg-white ${c.hoverBorder} shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-4 ${c.focusRing}`}
          >
            <span className={`font-display text-3xl sm:text-4xl font-semibold leading-none ${c.letter}`}>
              {item.letter}
            </span>
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-slate-500">
              /{item.sound}/
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}
